import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../../hooks/modal/useModal';
import type { GeneralPromptModalProps } from './modals';

export const GeneralPromptModalV2: React.FC<GeneralPromptModalProps> = ({
  description,
  onSubmit,
  onAbort,
  type,
  shouldAutomaticallyClose = true,
  customButtons,
}) => {
  const { hideModal } = useModal();
  const { t } = useTranslation();

  const { text, styling } = useMemo((): {
    text: string;
    styling: 'primary' | 'danger';
  } => {
    switch (type) {
      case 'delete': {
        return {
          text: t('general.buttons.delete'),
          styling: 'danger',
        };
      }
      case 'confirm': {
        return {
          text: t('general.buttons.confirm'),
          styling: 'primary',
        };
      }
      default: {
        return {
          text: t('general.buttons.submit'),
          styling: 'primary',
        };
      }
    }
  }, [t, type]);

  const renderButtons = () => {
    if (customButtons && customButtons.length > 0) {
      return (
        <>
          {/* Cancel always sits first as the secondary action */}
          <ButtonT
            type='button'
            styling='outline'
            size='xs'
            rounding='md'
            onClick={() => (onAbort ? onAbort() : hideModal())}>
            {t('general.buttons.cancel')}
          </ButtonT>

          {customButtons.map((button, index) => (
            <ButtonT
              key={index}
              type='button'
              styling={button.styling || 'primary'}
              size='xs'
              rounding='md'
              onClick={() => {
                // Close this modal BEFORE running the action: the action may open
                // another modal (e.g. the Voice AI agreement), and closing afterwards
                // would immediately dismiss that new modal too.
                shouldAutomaticallyClose && hideModal();
                button.onClick();
              }}>
              {button.text}
            </ButtonT>
          ))}
        </>
      );
    }

    return (
      <>
        <ButtonT
          type='button'
          styling='outline'
          size='xs'
          rounding='md'
          onClick={() => (onAbort ? onAbort() : hideModal())}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling={styling}
          size='xs'
          rounding='md'
          onClick={() => {
            onSubmit?.();
            shouldAutomaticallyClose && hideModal();
          }}>
          {text}
        </ButtonT>
      </>
    );
  };

  return (
    <div className='flex flex-col gap-5'>
      <p className='text-xs font-normal leading-relaxed text-gray-600'>
        {description}
      </p>
      <div className='flex items-center justify-end gap-2 border-t border-gray-200 pt-4'>
        {renderButtons()}
      </div>
    </div>
  );
};

export default GeneralPromptModalV2;
